import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { GatewayAPIError } from "../../app/api/client";
import { applyRuntimeLocale } from "../i18n/i18n";
import {
  applyDocumentTheme,
  normalizeThemePreference,
  THEME_PREFERENCES,
  writeCachedTheme,
  type ThemePreference,
} from "../theme/theme";
import { normalizeSupportedLocale, patchSelfProfile, type SelfProfile, type SupportedLocale } from "./profileApi";

export const PROFILE_LANGUAGE_OPTIONS: readonly SupportedLocale[] = ["ru", "en"];
export const PROFILE_THEME_OPTIONS: readonly ThemePreference[] = THEME_PREFERENCES;

export const PROFILE_SETTINGS_PANEL_CLASSES = {
  root: "profile-settings-panel",
  languageSelect: "profile-settings-language-select",
  themeSelect: "profile-settings-theme-select",
} as const;

interface LocaleSelectionResult {
  locale: SupportedLocale;
  updated: boolean;
}

interface UpdateProfileLocaleSelectionInput {
  currentLocale: SupportedLocale;
  requestedLocale: SupportedLocale;
  patchProfile: (input: { locale: SupportedLocale }) => Promise<Pick<SelfProfile, "locale">>;
  applyLocale: (locale: SupportedLocale) => Promise<void>;
  onLocaleApplied?: (locale: SupportedLocale) => void;
}

export async function updateProfileLocaleSelection(
  input: UpdateProfileLocaleSelectionInput,
): Promise<LocaleSelectionResult> {
  if (input.requestedLocale === input.currentLocale) {
    return {
      locale: input.currentLocale,
      updated: false,
    };
  }

  try {
    const response = await input.patchProfile({
      locale: input.requestedLocale,
    });
    const confirmedLocale = normalizeSupportedLocale(response.locale);
    await input.applyLocale(confirmedLocale);
    input.onLocaleApplied?.(confirmedLocale);

    return {
      locale: confirmedLocale,
      updated: true,
    };
  } catch {
    return {
      locale: input.currentLocale,
      updated: false,
    };
  }
}

interface ThemeSelectionResult {
  theme: ThemePreference;
  updated: boolean;
}

interface UpdateProfileThemeSelectionInput {
  currentTheme: ThemePreference;
  requestedTheme: ThemePreference;
  patchProfile: (input: { theme: ThemePreference }) => Promise<Pick<SelfProfile, "theme">>;
  applyTheme: (theme: ThemePreference) => void;
  persistTheme: (theme: ThemePreference) => void;
  onThemeApplied?: (theme: ThemePreference) => void;
}

export async function updateProfileThemeSelection(
  input: UpdateProfileThemeSelectionInput,
): Promise<ThemeSelectionResult> {
  if (input.requestedTheme === input.currentTheme) {
    return {
      theme: input.currentTheme,
      updated: false,
    };
  }

  try {
    const response = await input.patchProfile({
      theme: input.requestedTheme,
    });
    const confirmedTheme = normalizeThemePreference(response.theme);
    input.applyTheme(confirmedTheme);
    input.persistTheme(confirmedTheme);
    input.onThemeApplied?.(confirmedTheme);

    return {
      theme: confirmedTheme,
      updated: true,
    };
  } catch {
    return {
      theme: input.currentTheme,
      updated: false,
    };
  }
}

interface NicknameUpdateResult {
  nickname: string;
  updated: boolean;
  errorMessage: string | null;
}

interface DisplayNameUpdateResult {
  displayName: string;
  updated: boolean;
  errorMessage: string | null;
}

interface UpdateProfileDisplayNameInput {
  currentDisplayName: string;
  requestedDisplayName: string;
  patchProfile: (input: { display_name: string }) => Promise<Partial<Pick<SelfProfile, "display_name">>>;
  translate: (key: string) => string;
  onDisplayNameApplied?: (displayName: string) => void;
}

interface UpdateProfileNicknameInput {
  currentNickname: string;
  requestedNickname: string;
  patchProfile: (input: { nickname: string }) => Promise<Partial<Pick<SelfProfile, "nickname">>>;
  translate: (key: string) => string;
  onNicknameApplied?: (nickname: string) => void;
}

function normalizeNicknameDraft(value: string | null | undefined): string {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeDisplayNameDraft(value: string | null | undefined): string {
  return typeof value === "string" ? value.trim().split(/\s+/u).filter(Boolean).join(" ") : "";
}

function mapDisplayNameSaveError(error: unknown, translate: (key: string) => string): string {
  if (error instanceof GatewayAPIError && error.code === "invalid_request") {
    return translate("profileSettings.displayNameInvalidError");
  }
  return translate("profileSettings.displayNameSaveError");
}

function mapNicknameSaveError(error: unknown, translate: (key: string) => string): string {
  if (error instanceof GatewayAPIError) {
    if (error.code === "nickname_taken") {
      return translate("profileSettings.nicknameTakenError");
    }
    if (error.code === "invalid_request") {
      return translate("profileSettings.nicknameInvalidError");
    }
  }
  return translate("profileSettings.nicknameSaveError");
}

export async function updateProfileDisplayName(input: UpdateProfileDisplayNameInput): Promise<DisplayNameUpdateResult> {
  const currentDisplayName = normalizeDisplayNameDraft(input.currentDisplayName);
  const requestedDisplayName = normalizeDisplayNameDraft(input.requestedDisplayName);
  if (requestedDisplayName === currentDisplayName) {
    return {
      displayName: currentDisplayName,
      updated: false,
      errorMessage: null,
    };
  }
  if (requestedDisplayName === "") {
    return {
      displayName: currentDisplayName,
      updated: false,
      errorMessage: input.translate("profileSettings.displayNameInvalidError"),
    };
  }

  try {
    const response = await input.patchProfile({
      display_name: requestedDisplayName,
    });
    const confirmedDisplayName =
      typeof response.display_name === "string" ? normalizeDisplayNameDraft(response.display_name) : currentDisplayName;
    input.onDisplayNameApplied?.(confirmedDisplayName);
    return {
      displayName: confirmedDisplayName,
      updated: true,
      errorMessage: null,
    };
  } catch (error) {
    return {
      displayName: currentDisplayName,
      updated: false,
      errorMessage: mapDisplayNameSaveError(error, input.translate),
    };
  }
}

export async function updateProfileNickname(input: UpdateProfileNicknameInput): Promise<NicknameUpdateResult> {
  const currentNickname = normalizeNicknameDraft(input.currentNickname);
  const requestedNickname = normalizeNicknameDraft(input.requestedNickname);
  if (requestedNickname === currentNickname) {
    return {
      nickname: currentNickname,
      updated: false,
      errorMessage: null,
    };
  }

  try {
    const response = await input.patchProfile({
      nickname: requestedNickname,
    });
    const confirmedNickname =
      typeof response.nickname === "string" ? normalizeNicknameDraft(response.nickname) : currentNickname;
    input.onNicknameApplied?.(confirmedNickname);
    return {
      nickname: confirmedNickname,
      updated: true,
      errorMessage: null,
    };
  } catch (error) {
    return {
      nickname: currentNickname,
      updated: false,
      errorMessage: mapNicknameSaveError(error, input.translate),
    };
  }
}

type ProfileSettingsPatchInput = {
  display_name?: string;
  nickname?: string;
  locale?: SupportedLocale;
  theme?: ThemePreference;
};

type ProfileSettingsPatchResponse = Partial<Pick<SelfProfile, "display_name" | "nickname" | "locale" | "theme">>;

interface ProfileSettingsPanelProps {
  displayName?: string;
  nickname?: string;
  locale: SupportedLocale;
  theme?: ThemePreference;
  disabled?: boolean;
  patchProfile?: (input: ProfileSettingsPatchInput) => Promise<ProfileSettingsPatchResponse>;
  applyLocale?: (locale: SupportedLocale) => Promise<void>;
  applyTheme?: (theme: ThemePreference) => void;
  persistTheme?: (theme: ThemePreference) => void;
  onDisplayNameApplied?: (displayName: string) => void;
  onNicknameApplied?: (nickname: string) => void;
  onLocaleApplied?: (locale: SupportedLocale) => void;
  onThemeApplied?: (theme: ThemePreference) => void;
}

function localeOptionKey(locale: SupportedLocale): string {
  return locale === "ru" ? "profileSettings.optionRu" : "profileSettings.optionEn";
}

function themeOptionKey(theme: ThemePreference): string {
  return theme === "light" ? "profileSettings.optionLight" : "profileSettings.optionDark";
}

export function ProfileSettingsPanel({
  displayName = "",
  nickname = "",
  locale,
  theme = normalizeThemePreference(undefined),
  disabled = false,
  patchProfile = patchSelfProfile,
  applyLocale = applyRuntimeLocale,
  applyTheme = applyDocumentTheme,
  persistTheme = writeCachedTheme,
  onDisplayNameApplied,
  onNicknameApplied,
  onLocaleApplied,
  onThemeApplied,
}: ProfileSettingsPanelProps) {
  const { t } = useTranslation();
  const [confirmedDisplayName, setConfirmedDisplayName] = useState<string>(normalizeDisplayNameDraft(displayName));
  const [displayNameInput, setDisplayNameInput] = useState<string>(normalizeDisplayNameDraft(displayName));
  const [confirmedNickname, setConfirmedNickname] = useState<string>(normalizeNicknameDraft(nickname));
  const [nicknameInput, setNicknameInput] = useState<string>(normalizeNicknameDraft(nickname));
  const [selectedLocale, setSelectedLocale] = useState<SupportedLocale>(locale);
  const [selectedTheme, setSelectedTheme] = useState<ThemePreference>(normalizeThemePreference(theme));
  const [savingField, setSavingField] = useState<"displayName" | "nickname" | "locale" | "theme" | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const saving = savingField !== null;
  const normalizedDisplayNameInput = normalizeDisplayNameDraft(displayNameInput);
  const displayNameSaveDisabled =
    disabled || saving || normalizedDisplayNameInput === "" || normalizedDisplayNameInput === confirmedDisplayName;
  const nicknameSaveDisabled = disabled || saving || normalizeNicknameDraft(nicknameInput) === confirmedNickname;

  useEffect(() => {
    const normalized = normalizeDisplayNameDraft(displayName);
    setConfirmedDisplayName(normalized);
    setDisplayNameInput(normalized);
  }, [displayName]);

  useEffect(() => {
    const normalized = normalizeNicknameDraft(nickname);
    setConfirmedNickname(normalized);
    setNicknameInput(normalized);
  }, [nickname]);

  useEffect(() => {
    setSelectedLocale(locale);
  }, [locale]);

  useEffect(() => {
    setSelectedTheme(normalizeThemePreference(theme));
  }, [theme]);

  const onLocaleChange = async (event: ChangeEvent<HTMLSelectElement>) => {
    const requestedLocale = normalizeSupportedLocale(event.target.value);
    if (requestedLocale === selectedLocale) {
      return;
    }

    setSavingField("locale");
    setErrorMessage(null);
    setStatusMessage(null);
    const result = await updateProfileLocaleSelection({
      currentLocale: selectedLocale,
      requestedLocale,
      patchProfile: (input) => patchProfile(input),
      applyLocale,
      onLocaleApplied,
    });
    setSelectedLocale(result.locale);
    if (!result.updated) {
      setErrorMessage(t("profileSettings.saveError"));
    }
    setSavingField(null);
  };

  const onThemeChange = async (event: ChangeEvent<HTMLSelectElement>) => {
    const requestedTheme = normalizeThemePreference(event.target.value);
    if (requestedTheme === selectedTheme) {
      return;
    }

    setSavingField("theme");
    setErrorMessage(null);
    setStatusMessage(null);
    const result = await updateProfileThemeSelection({
      currentTheme: selectedTheme,
      requestedTheme,
      patchProfile: (input) => patchProfile(input),
      applyTheme,
      persistTheme,
      onThemeApplied,
    });
    setSelectedTheme(result.theme);
    if (!result.updated) {
      setErrorMessage(t("profileSettings.themeSaveError"));
    }
    setSavingField(null);
  };

  const onDisplayNameSave = async () => {
    setSavingField("displayName");
    setErrorMessage(null);
    setStatusMessage(null);
    const result = await updateProfileDisplayName({
      currentDisplayName: confirmedDisplayName,
      requestedDisplayName: displayNameInput,
      patchProfile: (input) => patchProfile(input),
      translate: t,
      onDisplayNameApplied,
    });
    setConfirmedDisplayName(result.displayName);
    setDisplayNameInput(result.displayName);
    if (result.updated) {
      setStatusMessage(t("profileSettings.displayNameSaved"));
    } else if (result.errorMessage !== null) {
      setErrorMessage(result.errorMessage);
    }
    setSavingField(null);
  };

  const onNicknameSave = async () => {
    setSavingField("nickname");
    setErrorMessage(null);
    setStatusMessage(null);
    const result = await updateProfileNickname({
      currentNickname: confirmedNickname,
      requestedNickname: nicknameInput,
      patchProfile: (input) => patchProfile(input),
      translate: t,
      onNicknameApplied,
    });
    setConfirmedNickname(result.nickname);
    setNicknameInput(result.nickname);
    if (result.updated) {
      setStatusMessage(t("profileSettings.nicknameSaved"));
    } else if (result.errorMessage !== null) {
      setErrorMessage(result.errorMessage);
    }
    setSavingField(null);
  };

  return (
    <section className={PROFILE_SETTINGS_PANEL_CLASSES.root} data-testid="profile-settings-panel">
      <div className="chat-shell-heading">
        <p className="chat-shell-eyebrow">{t("profileSettings.sectionEyebrow")}</p>
        <h2 className="chat-section-title">{t("profileSettings.sectionTitle")}</h2>
      </div>

      <label className="chat-field">
        <span>{t("profileSettings.displayNameLabel")}</span>
        <input
          data-testid="profile-display-name-input"
          value={displayNameInput}
          disabled={disabled || saving}
          autoComplete="name"
          maxLength={64}
          aria-describedby="profile-display-name-hint"
          onChange={(event) => setDisplayNameInput(event.target.value)}
        />
      </label>
      <button
        className="secondary-button"
        type="button"
        data-testid="profile-display-name-save"
        disabled={displayNameSaveDisabled}
        onClick={() => {
          void onDisplayNameSave();
        }}
      >
        {savingField === "displayName" ? t("profileSettings.displayNameSaving") : t("profileSettings.displayNameSave")}
      </button>
      <p id="profile-display-name-hint" className="muted-line">
        {savingField === "displayName"
          ? t("profileSettings.displayNameSaving")
          : t("profileSettings.displayNameHint")}
      </p>

      <label className="chat-field">
        <span>{t("profileSettings.nicknameLabel")}</span>
        <input
          data-testid="profile-nickname-input"
          value={nicknameInput}
          disabled={disabled || saving}
          autoComplete="nickname"
          maxLength={32}
          aria-describedby="profile-nickname-hint"
          onChange={(event) => setNicknameInput(event.target.value)}
        />
      </label>
      <button
        className="secondary-button"
        type="button"
        data-testid="profile-nickname-save"
        disabled={nicknameSaveDisabled}
        onClick={() => {
          void onNicknameSave();
        }}
      >
        {savingField === "nickname" ? t("profileSettings.nicknameSaving") : t("profileSettings.nicknameSave")}
      </button>
      <p id="profile-nickname-hint" className="muted-line">
        {savingField === "nickname" ? t("profileSettings.nicknameSaving") : t("profileSettings.nicknameHint")}
      </p>

      <label className="chat-field">
        <span>{t("profileSettings.selectLabel")}</span>
        <select
          className={PROFILE_SETTINGS_PANEL_CLASSES.languageSelect}
          data-testid="profile-language-select"
          value={selectedLocale}
          disabled={disabled || saving}
          onChange={(event) => {
            void onLocaleChange(event);
          }}
        >
          {PROFILE_LANGUAGE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {t(localeOptionKey(option))}
            </option>
          ))}
        </select>
      </label>

      <p className="muted-line">{savingField === "locale" ? t("profileSettings.saving") : t("profileSettings.selectHint")}</p>

      <label className="chat-field">
        <span>{t("profileSettings.themeLabel")}</span>
        <select
          className={PROFILE_SETTINGS_PANEL_CLASSES.themeSelect}
          data-testid="profile-theme-select"
          value={selectedTheme}
          disabled={disabled || saving}
          onChange={(event) => {
            void onThemeChange(event);
          }}
        >
          {PROFILE_THEME_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {t(themeOptionKey(option))}
            </option>
          ))}
        </select>
      </label>

      <p className="muted-line">{savingField === "theme" ? t("profileSettings.saving") : t("profileSettings.themeHint")}</p>
      {statusMessage === null ? null : <p className="muted-line">{statusMessage}</p>}
      {errorMessage === null ? null : <p className="inline-error">{errorMessage}</p>}
    </section>
  );
}
